import { useCan } from '../../core/authorization/PermissionProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddPhotoAlternateOutlined,
  ChatBubbleOutline,
  Close,
  DeleteOutline,
  DynamicFeed,
  Favorite,
  FavoriteBorder,
  KeyboardArrowDown,
  Lock,
  MoreVert,
  Public,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../app/store';
import { feedApi } from '../../core/api/client';
import type { FeedPost, PostVisibility } from '../../core/api/types';
import { fullName, timeAgo } from '../../core/utils/format';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { InitialAvatar } from '../../components/InitialAvatar';
import { FullPageLoader } from '../../components/FullPageLoader';
import { PHOTO_TYPES, uploadPhoto } from '../hub/ProfilePhotos';
import { PostPhotos } from '../public/PostCard';

const MAX_CONTENT = 4000;
/** PlatformAPI PublicPosts.MaxPhotos. */
const MAX_PHOTOS = 10;

interface PendingPhoto { file: File; preview: string }

export function FeedPage() {
  const canCreate = useCan("platform.feed", "create");
  const canDelete = useCan("platform.feed", "delete");
  const organizationId = useAppSelector((s) => s.session.organization.id);
  const profile = useAppSelector((s) => s.session.profile);

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [composeOpen, setComposeOpen] = useState(false);
  const [content, setContent] = useState('');
  // Public by default: these posts are what the Hub shows on the landing and on /social.
  const [visibility, setVisibility] = useState<PostVisibility>('Public');
  const [posting, setPosting] = useState(false);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const photosRef = useRef<PendingPhoto[]>([]);
  photosRef.current = photos;
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  const addPhotos = (files: FileList | null) => {
    const chosen = Array.from(files ?? []);
    const valid = chosen.filter((file) => PHOTO_TYPES.includes(file.type) && file.size > 0);
    if (valid.length < chosen.length) toast.error('Choose JPEG, PNG, WebP, GIF or AVIF photos.');
    const room = MAX_PHOTOS - photos.length;
    if (valid.length > room) toast.error(`A post can have at most ${MAX_PHOTOS} photos.`);
    setPhotos((current) => [...current, ...valid.slice(0, room).map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    setComposeOpen(true);
  };
  const removePhoto = (index: number) => setPhotos((current) => {
    URL.revokeObjectURL(current[index].preview);
    return current.filter((_, position) => position !== index);
  });
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  const [pendingDelete, setPendingDelete] = useState<FeedPost | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [moreMenuFor, setMoreMenuFor] = useState<string | null>(null);

  const load = useCallback(
    async (reset: boolean, cursor?: string | null) => {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setLoadError(null);
      try {
        const page = await feedApi.feed(reset ? null : (cursor ?? nextCursor));
        setPosts((current) => {
          if (reset) return page.items;
          const known = new Set(current.map((p) => p.id));
          return [...current, ...page.items.filter((p) => !known.has(p.id))];
        });
        setNextCursor(page.nextCursor);
      } catch {
        setLoadError('The feed could not be loaded right now.');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [nextCursor],
  );

  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const toggleLike = async (post: FeedPost) => {
    const nextLiked = !post.likedByMe;
    if (nextLiked ? !canCreate : !canDelete) return;
    // Optimistic update with rollback, mirroring the Poseidon feed behavior.
    setPosts((current) =>
      current.map((p) =>
        p.id === post.id
          ? { ...p, likedByMe: nextLiked, likeCount: Math.max(0, p.likeCount + (nextLiked ? 1 : -1)) }
          : p,
      ),
    );
    try {
      if (nextLiked) await feedApi.like(post.id);
      else await feedApi.unlike(post.id);
    } catch {
      setPosts((current) =>
        current.map((p) =>
          p.id === post.id
            ? { ...p, likedByMe: post.likedByMe, likeCount: post.likeCount }
            : p,
        ),
      );
      toast.error('Could not update the like. Please try again.');
    }
  };

  const handleCompose = async () => {
    if (!canCreate) return;
    const trimmed = content.trim();
    if (!trimmed && photos.length === 0) return;
    setPosting(true);
    if (photos.length > 0) {
      // Photo posts: upload each photo, publish with their ids, then reload to show the served variants.
      try {
        const mediaIds: string[] = [];
        for (const photo of photos) mediaIds.push(await uploadPhoto('/feed/posts/media/upload-session', photo.file));
        await feedApi.createPost(trimmed, visibility, mediaIds);
        photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
        setPhotos([]);
        setContent('');
        setComposeOpen(false);
        toast.success('Post published.');
        await load(true);
      } catch (error) {
        toast.error(error instanceof Error && error.message ? error.message : 'Your post could not be published.');
      } finally {
        setPosting(false);
      }
      return;
    }
    const optimisticId = `new-${Date.now()}`;
    const optimistic: FeedPost = {
      id: optimisticId,
      authorUserId: profile.id,
      content: trimmed,
      visibility,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      author: { firstName: profile.firstName, lastName: profile.lastName, avatarObjectKey: null },
      commentCount: 0,
      likeCount: 0,
      likedByMe: false,
    };
    setPosts((current) => [optimistic, ...current]);
    setContent('');
    setComposeOpen(false);
    try {
      const created = await feedApi.createPost(trimmed, visibility);
      setPosts((current) => current.map((p) => (p.id === optimisticId ? { ...p, id: created.id } : p)));
      toast.success('Post published.');
    } catch {
      setPosts((current) => current.filter((p) => p.id !== optimisticId));
      toast.error('Your post could not be published.');
    } finally {
      setPosting(false);
    }
  };

  const addComment = async (postId: string, commentContent: string) => {
    await feedApi.addComment(postId, commentContent);
    setPosts((current) =>
      current.map((p) => (p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p)),
    );
    toast.success('Comment added.');
  };

  const confirmDelete = async () => {
    if (!canDelete) return;
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await feedApi.deletePost(pendingDelete.id);
      setPosts((current) => current.filter((p) => p.id !== pendingDelete.id));
      toast.success('Post deleted.');
      setPendingDelete(null);
    } catch {
      toast.error('The post could not be deleted.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <PageHeader title="Posts" subtitle="Public posts appear on the Architech home page and in Social; Organization posts stay inside your team." />

      {loadError ? (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button onClick={() => void load(true)}>Retry</Button>}>
          {loadError}
        </Alert>
      ) : null}

      {/* Composer — visually mirrors the Poseidon composer block. */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <InitialAvatar name={`${profile.firstName} ${profile.lastName}`} />
            <Box sx={{ flexGrow: 1 }}>
              <textarea
                ref={composerRef}
                aria-label="Write a post"
                rows={composeOpen ? 4 : 1}
                placeholder="What is happening around you?…"
                value={content}
                maxLength={MAX_CONTENT}
                onChange={(event) => setContent(event.target.value)}
                onFocus={() => setComposeOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                    event.preventDefault();
                    void handleCompose();
                  }
                }}
                style={{
                  width: '100%',
                  border: '1px solid rgba(15,23,42,0.12)',
                  borderRadius: 6,
                  padding: '10px 12px',
                  font: 'inherit',
                  resize: 'vertical',
                  minHeight: 44,
                }}
              />
              {photos.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {photos.map((photo, index) => (
                    <Box key={photo.preview} sx={{ position: 'relative', width: 76, height: 76, borderRadius: 1.5, overflow: 'hidden', bgcolor: 'action.hover' }}>
                      <img src={photo.preview} alt={`Photo ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      <IconButton size="small" aria-label={`Remove photo ${index + 1}`} onClick={() => removePhoto(index)} disabled={posting}
                        sx={{ position: 'absolute', top: 2, right: 2, p: 0.25, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.75)' } }}>
                        <Close sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                <Tooltip title={`Add up to ${MAX_PHOTOS} photos`}>
                  <span>
                    <Button size="small" component="label" startIcon={<AddPhotoAlternateOutlined />} disabled={!canCreate || posting || photos.length >= MAX_PHOTOS}>
                      Photos
                      <input hidden type="file" multiple accept={PHOTO_TYPES.join(',')} aria-label="Add photos"
                        onChange={(event) => { addPhotos(event.target.files); event.target.value = ''; }} />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Who can see this post">
                  <Button
                    size="small"
                    startIcon={visibility === 'Public' ? <Public /> : <Lock />}
                    onClick={() => setVisibility((v) => (v === 'Public' ? 'Organization' : 'Public'))}
                  >
                    {visibility === 'Public' ? 'Public' : 'Organization'}
                  </Button>
                </Tooltip>
                <Typography variant="caption" color={content.length > MAX_CONTENT * 0.9 ? 'error' : 'text.secondary'} sx={{ ml: 'auto' }}>
                  {content.length}/{MAX_CONTENT}
                </Typography>
                {composeOpen ? (
                  <>
                    <Button size="small" color="inherit" disabled={posting} onClick={() => {
                      photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
                      setPhotos([]); setContent(''); setComposeOpen(false);
                    }}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" disabled={!canCreate || (!content.trim() && photos.length === 0) || posting} onClick={() => void handleCompose()}>
                      {posting ? 'Publishing…' : 'Publish'}
                    </Button>
                  </>
                ) : null}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <FullPageLoader label="Loading feed" />
      ) : posts.length === 0 ? (
        <Card variant="outlined">
          <EmptyState
            icon={<DynamicFeed sx={{ fontSize: 44 }} />}
            title="No posts yet"
            hint="Be the first to share an update with your organization."
          />
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              mine={canDelete && post.authorUserId === profile.id}
              onToggleLike={() => void toggleLike(post)}
              onAddComment={(text) => addComment(post.id, text)}
              moreOpen={moreMenuFor === post.id}
              onMoreOpen={() => setMoreMenuFor(post.id)}
              onMoreClose={() => setMoreMenuFor(null)}
              onDelete={() => setPendingDelete(post)}
            />
          ))}
        </Box>
      )}

      {nextCursor ? (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={loadingMore ? <CircularProgress size={18} /> : <KeyboardArrowDown />}
            disabled={loadingMore}
            onClick={() => void load(false, nextCursor)}
          >
            {loadingMore ? 'Loading…' : 'Load more posts'}
          </Button>
        </Box>
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------

interface PostCardProps {
  post: FeedPost;
  mine: boolean;
  onToggleLike: () => void;
  onAddComment: (content: string) => Promise<void>;
  moreOpen: boolean;
  onMoreOpen: () => void;
  onMoreClose: () => void;
  onDelete: () => void;
}

function PostCard({ post, mine, onToggleLike, onAddComment, moreOpen, onMoreOpen, onMoreClose, onDelete }: PostCardProps) {
  const canCreate = useCan("platform.feed", "create");
  const canDelete = useCan("platform.feed", "delete");
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const authorName = fullName(post.author.firstName, post.author.lastName);

  const submitComment = async () => {
    const trimmed = comment.trim();
    if (!trimmed) return;
    setSubmittingComment(true);
    try {
      await onAddComment(trimmed);
      setComment('');
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ overflow: 'visible' }}>
      <CardContent sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <InitialAvatar name={authorName} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {authorName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timeAgo(post.createdAt)}
              {post.visibility === 'Public' ? ' · Public' : ''}
            </Typography>
          </Box>
          {mine ? (
            <Box sx={{ ml: 'auto' }}>
              <IconButton size="small" aria-label="Post options" onClick={moreOpen ? onMoreClose : onMoreOpen}>
                <MoreVert fontSize="small" />
              </IconButton>
              <Menu
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={moreOpen}
                onClose={onMoreClose}
              >
                <MenuItem onClick={() => { onMoreClose(); onDelete(); }} sx={{ color: 'error.main' }}>
                  <DeleteOutline fontSize="small" sx={{ mr: 1 }} /> Delete post
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Box>

        <Typography
          variant="body1"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 1, px: 0.5 }}
        >
          {post.content}
        </Typography>
      </CardContent>
      {post.media && post.media.length > 0 ? <PostPhotos media={post.media} alt={post.content.slice(0, 100) || authorName} /> : null}

      <Divider />
      {/* Equal-flex action bar like .post-stats */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tooltip title={post.likedByMe ? 'Unlike' : 'Like'}>
          <Button
            fullWidth
            onClick={onToggleLike} disabled={post.likedByMe ? !canDelete : !canCreate}
            startIcon={post.likedByMe ? <Favorite /> : <FavoriteBorder />}
            sx={{ color: post.likedByMe ? 'primary.main' : 'text.secondary', borderRadius: 0 }}
          >
            {post.likeCount}
          </Button>
        </Tooltip>
        <Tooltip title={expanded ? 'Hide comment summary' : 'Show comment summary'}>
          <Button
            fullWidth
            onClick={() => setExpanded((v) => !v)}
            startIcon={<ChatBubbleOutline />}
            sx={{ color: expanded ? 'primary.main' : 'text.secondary', borderRadius: 0 }}
          >
            {post.commentCount}
          </Button>
        </Tooltip>
      </Box>

      {expanded ? (
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {post.commentCount} comment{post.commentCount === 1 ? '' : 's'} on this post. Comment threads appear here
            once the PlatformAPI comment-read endpoints ship.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Add a comment…"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void submitComment();
                }
              }}
            />
            <Button
              size="small"
              variant="contained"
              disabled={!canCreate || !comment.trim() || submittingComment}
              onClick={() => void submitComment()}
            >
              {submittingComment ? 'Posting…' : 'Post'}
            </Button>
          </Box>
        </Box>
      ) : null}
    </Card>
  );
}
